import {
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography,
    Grid,
    Link,
    Checkbox,
    FormControlLabel
} from "@mui/material";
import {
    PaymentElement,
    useElements,
    useStripe
} from "@stripe/react-stripe-js";
import { CSSProperties, useEffect, useState } from "react";
import { EventPublicDetailsInfoModel } from "../../../data/Models/EventPublicDetailsInfoModel";
import { PreviewOrderEventTicketsRequest } from "../../../data/Models/Requests/PreviewOrderEventTicketsRequest";
import { OrderEventTicketsResponse } from "../../../data/Models/Responses/OrderEventTicketsResponse";
import { theme } from "../../../theme/theme";
import OrderPreviewInfo from "../OrderPreviewInfo/OrderPreviewInfo";
import { Link as RouterLink } from "react-router-dom";

const payoutElementStyles: CSSProperties = {
    backgroundColor: theme.palette.nrbyColors.faintGray,
    color: theme.palette.nrbyColors.invertedColor,
    justifyContent: "center",
    display: "flex",
    padding: "16px",
    paddingTop: "0",
};

interface StripePaymentElementProps {
    event: EventPublicDetailsInfoModel | undefined;
    orderPreview: OrderEventTicketsResponse;
    order: PreviewOrderEventTicketsRequest | undefined;
    goBackStep: () => void;
}

const StripePaymentElement = ({
    event,
    orderPreview,
    order,
    goBackStep,
}: StripePaymentElementProps) => {
    const [message, setMessage] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<any>(false);
    const [paymentAttempts, setPaymentAttemps] = useState<any>(0);
    const [optInMessaging, setOptInMessaging] = useState<boolean>(false);
    const stripe = useStripe();
    const elements = useElements();

    useEffect(() => {
        if (!stripe) {
            return;
        }

        const clientSecret = orderPreview.paymentIntentClientSecret;

        if (!clientSecret) {
            return;
        }

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
            switch (paymentIntent?.status) {
                case "succeeded":
                    setMessage("Payment succeeded!");
                    break;
                case "processing":
                    setMessage("Your payment is processing.");
                    break;
                case "requires_payment_method":
                    if (paymentAttempts > 0) {
                        setMessage(
                            "Your payment was not successful, please try again."
                        );
                    }
                    break;
                default:
                    setMessage("Something went wrong.");
                    break;
            }
        });
    }, [stripe]);

    const HandleSubmit = async () => {
        if (!stripe || !elements) {
            return;
        }

        setIsLoading(true);

        let errored = false;

        var currentDateTime = new Date();

        var orderExpirationDateTime = new Date(
            orderPreview.deletePendingOrderDateTimeUTC!
        );
        if (orderExpirationDateTime < currentDateTime) {
            setMessage(
                "Your order has expired. Please go back to the beginning and try again."
            );
            errored = true;
        } else {
            setMessage("");
        }

        if (errored) {
            setIsLoading(false);
            return;
        }

        const searchParams = new URLSearchParams();
        searchParams.append("orderId", orderPreview.orderId);
        searchParams.append("firstName", order!.firstName);
        searchParams.append("lastName", order!.lastName);
        searchParams.append("email", order!.email);
        searchParams.append("phone", order!.phoneNumber);
        searchParams.append("dateTimeSubmitted", currentDateTime.toString());
        searchParams.append("optInMessaging", optInMessaging.toString());

        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                // Make sure to change this to your payment completion page
                return_url: `${
                    process.env.REACT_APP_BASE_URL
                }/orderconfirmation?${searchParams.toString()}`,
            },
        });

        setPaymentAttemps((previous: number) => previous + 1);

        if (error.type === "card_error" || error.type === "validation_error") {
            setMessage(error.message);
        } else {
            setMessage("An unexpected error occured.");
        }

        setIsLoading(false);
    };

    const HandleBack = () => {
        goBackStep();
    };

    return (
        <Stack id="payment-form">
            <PaymentElement id="payment-element" />
            <OrderPreviewInfo orderPreview={orderPreview} event={event} />
            <div style={{ width: "100%", padding: "16px 0px 16px 0px" }}>
                <Stack direction="row" justifyContent="flex-start" style={{ marginLeft: "10px" }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: 1.2,
                        }}
                    >
                        By placing this order I agree to the Nrby Events&nbsp;
                        <Link
                            underline="hover"
                            color="tangerine"
                            target="_blank"
                            component={RouterLink}
                            to="/terms"
                            sx={{ fontWeight: 400, fontSize: "14px", lineHeight: 1.2 }}
                        >
                            Terms of Service
                        </Link>
                        &nbsp;&&nbsp;
                        <Link
                            underline="hover"
                            color="tangerine"
                            target="_blank"
                            component={RouterLink}
                            to="/privacy"
                            sx={{ fontWeight: 400, fontSize: "14px", lineHeight: 1.2 }}
                        >
                            Privacy Policy
                        </Link>
                        .
                    </Typography>
                </Stack>
                <Stack direction="row" justifyContent="flex-start" style={{ marginLeft: "10px", marginTop: "10px" }}>
                    <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 400,
                                fontSize: "14px",
                                lineHeight: 1.2,
                                cursor: "pointer",
                            }}
                            onClick={() => {
                                setOptInMessaging(!optInMessaging);
                            }}
                        >
                         As set forth in our Terms of Service, we may use your information for email marketing, including promotions and updates on our own or third-party products. You can opt out of our marketing emails anytime. <br /><strong>All sales are non-refundable.</strong>
                    </Typography>
                </Stack>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={optInMessaging}
                            defaultChecked={false}
                            sx={{
                                color: "black",
                                '&.Mui-checked': { color: "black" },
                                '& .MuiSvgIcon-root': { border: '1px solid black' },
                            }}
                        />
                    }
                    label={
                        <div style={{ fontWeight: 400, fontSize: "14px", lineHeight: 1.2, width: "100%", padding: "8px 0" }}>
                            Opt-in to receive text message updates and promotions from Nrby Events and its event creators. This can include FREE TICKETS and DISCOUNTS to future events.
                            Message frequency varies. Message & data rates may apply. You can opt out at any time by replying STOP.
                        </div>
                    }
                    onChange={(event, checked) => setOptInMessaging(checked)}
                    sx={{
                        alignItems: "flex-start",
                        width: "100%",
                        m: 0,
                        p: 0,
                        '& .MuiFormControlLabel-label': {
                            width: "100%",
                            display: "block",
                            cursor: "pointer",
                        }
                    }}
                />
            </div>
            <div style={{ width: "100%", textAlign: "center", paddingTop: "16px" }}>
                {!isLoading && (
                    <Grid container xs={12}>
                        <Grid item xs={6}>
                            <Stack direction="row" justifyContent="flex-end" style={{ marginRight: "10px" }}>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    style={{
                                        height: "55px",
                                        textAlign: "center",
                                        width: "100%",
                                        fontSize: "18px",
                                    }}
                                    sx={{
                                        width: { xs: "100%", md: "auto" },
                                    }}
                                    onClick={HandleBack}
                                >
                                    &lt; Back
                                </Button>
                            </Stack>
                        </Grid>
                        <Grid item xs={6}>
                            <Stack direction="row" justifyContent="flex-start" style={{ marginLeft: "10px" }}>
                                <Button
                                    color="tangerine"
                                    variant="contained"
                                    style={{
                                        height: "55px",
                                        textAlign: "center",
                                        whiteSpace: "nowrap",
                                        width: "100%",
                                        fontSize: "18px"
                                    }}
                                    sx={{
                                        width: { xs: "100%", md: "auto" }
                                    }}
                                    disabled={isLoading || !stripe || !elements}
                                    onClick={HandleSubmit}
                                >
                                    Place Order
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                )}

                {isLoading && (
                    <Box alignContent="center" sx={{ mt: 4 }}>
                        <CircularProgress color="inherit" />
                    </Box>
                )}
            </div>

            {message && (
                <Box
                    id="payment-message"
                    style={{
                        color: theme.palette.nrbyColors.tangerine,
                        marginTop: "16px",
                    }}
                >
                    {message}
                </Box>
            )}
        </Stack>
    );
};

export default StripePaymentElement;
